import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Avatar,
  Button,
  Input,
  Select,
  Space,
  Table,
  Tag,
  Popconfirm,
  MessagePlugin,
} from "tdesign-react";
import type { PrimaryTableCol, PageInfo } from "tdesign-react";
import { SearchIcon, RefreshIcon, UserIcon } from "tdesign-icons-react";
import {
  AccountStatus,
  deleteAccount,
  fetchAccountList,
  getAvatarUrl,
  getNickname,
  updateAccount,
  type Account,
  type UpdateAccountPayload,
} from "@/services/account";
import { formatDateTime } from "@/utils/datetime";
import AccountFormDialog, { type AccountFormValue } from "./AccountFormDialog";
import "./AccountList.css";

/** 状态 → 标签主题（避免嵌套三元，用查表法） */
const STATUS_TAG: Record<
  AccountStatus,
  { theme: "warning" | "success" | "danger"; label: string }
> = {
  [AccountStatus.PENDING]: { theme: "warning", label: "待启用" },
  [AccountStatus.APPROVED]: { theme: "success", label: "启用" },
  [AccountStatus.REJECTED]: { theme: "danger", label: "禁用" },
};

const STATUS_OPTIONS = [
  { label: "全部", value: "" },
  { label: "待启用", value: AccountStatus.PENDING },
  { label: "启用", value: AccountStatus.APPROVED },
  { label: "禁用", value: AccountStatus.REJECTED },
];

const AccountList = () => {
  const [loading, setLoading] = useState(false);
  const [list, setList] = useState<Account[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const [keyword, setKeyword] = useState("");
  const [searchKeyword, setSearchKeyword] = useState("");
  const [statusFilter, setStatusFilter] = useState<AccountStatus | "">("");

  // —— 编辑表单（合并了原「修改状态」弹窗，一站式编辑资料 + 状态） ——
  const [editDialogVisible, setEditDialogVisible] = useState(false);
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [editing, setEditing] = useState<Account | null>(null);

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchAccountList({
        page,
        pageSize,
        keyword: searchKeyword,
        status: statusFilter === "" ? undefined : statusFilter,
      });
      setList(res.list);
      setTotal(res.total);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, searchKeyword, statusFilter]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  const handleSearch = () => {
    setPage(1);
    setSearchKeyword(keyword.trim());
  };

  const handleReset = () => {
    setKeyword("");
    setStatusFilter("");
    setPage(1);
    setSearchKeyword("");
    // 即使过滤条件没有发生变化（loadList 依赖未变 → useEffect 不会再触发），
    // 也强制刷新一次，保证用户点击「重置」始终有反馈。
    loadList();
  };

  const handleOpenEdit = (record: Account) => {
    setEditing(record);
    setEditDialogVisible(true);
  };

  const handleDelete = async (record: Account) => {
    try {
      await deleteAccount(record.id);
      MessagePlugin.success("删除成功");
      if (list.length === 1 && page > 1) {
        setPage(page - 1);
      } else {
        loadList();
      }
    } catch {
      /* 拦截器统一提示 */
    }
  };

  const handleEditSubmit = async (payload: AccountFormValue) => {
    if (!editing) return;
    setEditSubmitting(true);
    try {
      // 合并弹窗一次性提交资料 + 状态；后端会自动识别 status 是否变化决定是否写审计字段
      const data: UpdateAccountPayload = { id: editing.id, ...payload };
      await updateAccount(data);
      MessagePlugin.success("更新成功");
      setEditDialogVisible(false);
      setEditing(null);
      loadList();
    } finally {
      setEditSubmitting(false);
    }
  };

  const columns = useMemo<PrimaryTableCol<Account>[]>(
    () => [
      { colKey: "id", title: "ID", width: 70 },
      {
        colKey: "status",
        title: "状态",
        width: 100,
        cell: ({ row }) => {
          const cfg = STATUS_TAG[row.status];
          return (
            <Tag theme={cfg.theme} variant="light">
              {cfg.label}
            </Tag>
          );
        },
      },
      {
        colKey: "nickname",
        title: "微信昵称",
        width: 180,
        cell: ({ row }) => {
          const nickname = getNickname(row);
          const avatarUrl = getAvatarUrl(row);
          // 微信头像 URL 偶发失效（换绑/清理），image 加载失败时回退到用户图标占位。
          return (
            <Space size="small" align="center">
              <Avatar size="28px" image={avatarUrl || undefined} icon={<UserIcon />} />
              <span>{nickname || "-"}</span>
            </Space>
          );
        },
      },
      {
        colKey: "name",
        title: "姓名",
        width: 100,
        cell: ({ row }) => row.name || "-",
      },
      {
        colKey: "organization",
        title: "所属单位",
        width: 140,
        cell: ({ row }) => row.organization || "-",
      },
      {
        colKey: "phone",
        title: "手机号",
        width: 130,
        cell: ({ row }) => row.phone || "-",
      },
      {
        colKey: "createdAt",
        title: "注册时间",
        width: 120,
        cell: ({ row }) => formatDateTime(row.createdAt),
      },
      {
        colKey: "auditAt",
        title: "状态变更时间",
        width: 140,
        cell: ({ row }) => formatDateTime(row.auditAt),
      },
      {
        colKey: "auditBy",
        title: "操作人",
        width: 150,
        cell: ({ row }) => row.auditBy || "-",
      },
      {
        colKey: "operation",
        title: "操作",
        fixed: "right",
        cell: ({ row }) => (
          <Space size="small">
            <Button
              theme="primary"
              variant="text"
              onClick={() => handleOpenEdit(row)}
            >
              编辑
            </Button>
            <Popconfirm
              content={`确定删除账号「${
                getNickname(row) || row.name || row.openid
              }」吗？`}
              onConfirm={() => handleDelete(row)}
            >
              <Button theme="danger" variant="text">
                删除
              </Button>
            </Popconfirm>
          </Space>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [list, page]
  );

  const handlePageChange = (info: PageInfo) => {
    setPage(info.current);
    setPageSize(info.pageSize);
  };

  return (
    <div className="account-list-page">
      <div className="account-list-toolbar">
        <Space>
          <Input
            value={keyword}
            onChange={(val) => setKeyword(val as string)}
            placeholder="搜索 姓名 / 手机号 / 单位 / openid"
            clearable
            style={{ width: 360 }}
            onEnter={handleSearch}
          />
          <Select
            value={statusFilter}
            onChange={(v) => {
              setStatusFilter((v as AccountStatus | "") ?? "");
              setPage(1);
            }}
            options={STATUS_OPTIONS}
            style={{ width: 140 }}
            placeholder="状态"
          />
          <Button icon={<SearchIcon />} onClick={handleSearch}>
            搜索
          </Button>
          <Button
            variant="outline"
            icon={<RefreshIcon />}
            onClick={handleReset}
          >
            重置
          </Button>
        </Space>
      </div>

      <Table
        rowKey="id"
        data={list}
        columns={columns}
        loading={loading}
        bordered
        stripe
        size="medium"
        pagination={{
          current: page,
          pageSize,
          total,
          showJumper: true,
          pageSizeOptions: [10, 20, 50, 100],
          onChange: handlePageChange,
        }}
      />

      <AccountFormDialog
        visible={editDialogVisible}
        loading={editSubmitting}
        initialValue={editing}
        onCancel={() => {
          setEditDialogVisible(false);
          setEditing(null);
        }}
        onSubmit={handleEditSubmit}
      />
    </div>
  );
};

export default AccountList;
